export type ForceOrganisationViewMode = 'faction' | 'custom';

export interface ForceFolder {
    id: string;
    name: string;
    parentId: string | null;
    sortOrder?: number;
}

export interface ForceOrganisationItem {
    forceId: string;
    type: 'own' | 'shared';
    folderId: string | null;
    sortOrder: number;
}

export interface ForceOrganisationDocument {
    id: string;
    gameId: string;
    viewMode: ForceOrganisationViewMode;
    folders: ForceFolder[];
    items: ForceOrganisationItem[];
}

/**
 * Moves several forces into one destination in a single pass — the bulk twin of
 * ForceOrganisationService.moveForceToFolder. Kept here as a pure function so the
 * sortOrder arithmetic is testable without a Store; the service is a thin wrapper
 * that dispatches the result once, rather than once per force.
 *
 * Arrivals are appended after whatever is already in the destination, in the order
 * given. A force already in the destination is left exactly where it is, so
 * including it in a selection never reshuffles it.
 */
export function assignForcesToFolder(
    items: ForceOrganisationItem[],
    forceIds: string[],
    type: 'own' | 'shared',
    folderId: string | null
): ForceOrganisationItem[] {
    const moving = forceIds.filter((id) => {
        const existing = items.find((i) => i.forceId === id);
        return !existing || existing.folderId !== folderId;
    });
    if (!moving.length) {
        return [...items];
    }

    // -1000 so the first arrival into an empty destination lands on 0, matching
    // createBootstrapDocument's 1000-step convention.
    const destinationMax = items
        .filter((i) => i.folderId === folderId && !moving.includes(i.forceId))
        .reduce((max, i) => Math.max(max, i.sortOrder), -1000);

    const newOrders = new Map<string, number>();
    moving.forEach((id, index) => newOrders.set(id, destinationMax + (index + 1) * 1000));

    const result = items.map((item) =>
        newOrders.has(item.forceId) ? { ...item, folderId, sortOrder: newOrders.get(item.forceId)! } : item
    );

    for (const id of moving) {
        if (!items.some((i) => i.forceId === id)) {
            result.push({ forceId: id, type, folderId, sortOrder: newOrders.get(id)! });
        }
    }

    return result;
}

/**
 * Renumbers one scope's sortOrders into name order, discarding whatever custom
 * arrangement was there — the caller is expected to have warned first.
 *
 * `scope` is null for the root of the custom view, or a folder id when the user
 * is inside that folder. Only that scope is touched: sorting the root leaves
 * each folder's contents alone, and vice versa.
 *
 * At the root, folders are laid out as a leading alphabetical block and the
 * loose forces follow, since the list renders folders and root forces as
 * siblings ordered by the same sortOrder space.
 */
export function sortScopeAlphabetically(
    items: ForceOrganisationItem[],
    folders: ForceFolder[],
    forceNames: Map<string, string>,
    scope: string | null
): { items: ForceOrganisationItem[]; folders: ForceFolder[] } {
    // Names a user never sees sorted (a force whose record didn't load) go last
    // rather than being dropped or jumbled to the front by an empty string.
    const byName = (a: string | undefined, b: string | undefined): number => {
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    };

    const scopeItems = items.filter((i) => (i.folderId ?? null) === scope);
    const untouched = items.filter((i) => (i.folderId ?? null) !== scope);

    if (scope !== null) {
        const renumbered = [...scopeItems]
            .sort((a, b) => byName(forceNames.get(a.forceId), forceNames.get(b.forceId)))
            .map((item, index) => ({ ...item, sortOrder: index * 1000 }));
        return { items: [...untouched, ...renumbered], folders: [...folders] };
    }

    const sortedFolders = [...folders]
        .sort((a, b) => byName(a.name, b.name))
        .map((folder, index) => ({ ...folder, sortOrder: index * 1000 }));

    const forcesStart = sortedFolders.length * 1000;
    const renumbered = [...scopeItems]
        .sort((a, b) => byName(forceNames.get(a.forceId), forceNames.get(b.forceId)))
        .map((item, index) => ({ ...item, sortOrder: forcesStart + index * 1000 }));

    return { items: [...untouched, ...renumbered], folders: sortedFolders };
}

export function createBootstrapDocument(gameId: string, userId: string, forces: { id: string }[]): ForceOrganisationDocument {
    return {
        id: `${userId}:${gameId}`,
        gameId,
        viewMode: 'faction',
        folders: [],
        items: forces.map((f, i) => ({
            forceId: f.id,
            type: 'own',
            folderId: null,
            sortOrder: i * 1000
        }))
    };
}
